import React, { useEffect, useRef, useState } from 'react';
import './reward-preset.styles.scss';
import { getUniqueDocumentFromDb } from '@/utils/getDataFromDb';
import Modal, { ModalAlert } from '@/components/modal/modal.component';
import { useSelector } from 'react-redux';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { RandomString } from '@/utils/randomString';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { format, formatDate, set } from 'date-fns';
import ToggleInput from '@/components/SHARED/toggle-input/toggle-input.component';
import { useLocation } from 'react-router-dom';

const RewardPresetModal = ({
  gameData,
  mode,
  setMode,
  presetToSave,
  setPresetToLoad
}) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [existingPresets, setExistingPresets] = useState({});
  const [alertMessage, setAlertMessage] = useState(null);
  const [presetsFetched, setPresetsFetched] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [loading, setLoading] = useState(false);
  const dataRef = useRef();
  const chosenPresetRef = useRef();
  const location = useLocation();

  useEffect(() => {
    if (!gameData) return;
    setShowModal(!!mode);
    if (!presetsFetched) {
      getSavedPresets();
      setPresetsFetched(true);
    }
  }, [gameData, mode])

  function getSavedPresets() {
    getUniqueDocumentFromDb({
      'collection': `games/${gameData.gameId}/` +
        `rewardPresets`,
      'docId': `${gameData.gameId}_presets`,
      'once': true,
      'callback': callback
    })
    function callback(data) {
      console.log(data);
      setExistingPresets(data);
    }
  }

  function saveData(key, value) {
    dataRef.current = {
      ...dataRef.current,
      ...{ [key]: value }
    }
    setFormData(dataRef.current);
    console.log(dataRef.current);
  }

  async function savePreset() {
    if (location.pathname.includes('/preview/manager/')) {
      setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
      return;
    }
    setLoading(true);
    const tempData = { ...dataRef.current };
    const now = new Date().getTime();
    if (!tempData.name) {
      setAlertMessage('Please enter a name for the preset.');
      return;
    }
    console.log(presetToSave);
    const newObj = {};
    for (let key of Object.keys(presetToSave)) {
      if (!key.includes('opt_')) continue;
      newObj[key] = presetToSave[key];
    }
    newObj.name = tempData.name;
    newObj.ts_created = now;
    newObj.appId = userProfile.appId;
    newObj.id = `${now}_${RandomString(8)}_preset`
    if (tempData.saveRewardTitle) {
      newObj.rewardTitle = presetToSave.title;
    }
    console.log(newObj);
    const res = await UseCloudFunction(
      'saveRewardPreset',
      {
        'gameId': gameData.gameId,
        'preset': newObj
      }
    );
    console.log(res);
    setLoading(false);
    if (res.error) {
      setAlertMessage(`Something went wrong. Error: ${res.error.message}`);
      return;
    }
    let tempPresets = { ...existingPresets };
    tempPresets[newObj.id] = newObj;
    setExistingPresets(tempPresets);
    setShowModal(false);
    dataRef.current = {}
    setFormData(dataRef.current);
  }

  function choosePreset(preset) {
    chosenPresetRef.current = { ...preset };
    setSelectedPreset(preset.id);
  }

  return (
    <div className="reward-preset-modal">
      {
        (mode === 'save')
          ?
          <Modal
            show={showModal}
            cancel={() => setShowModal(false)}
            text="Save This Reward Preset" >
            <div className='save-div'>
              <div className='meta'>
                {existingPresets ? Object.values(existingPresets).length : 0}/100
              </div>
              <p>Enter a name for your new reward preset.</p>
              <div className='field'>
                <input
                  type="text"
                  placeholder="Enter preset name"
                  value={formData.name ?? ''}
                  onChange={e => saveData('name', e.target.value)} />
              </div>
              <div className='g-space-1' ></div>
              {
                (presetToSave.title) &&
                <div className='field with-toggle'>
                  <label>Save "{presetToSave.title}" as the reward title with this preset?</label>
                  <ToggleInput
                    contentKey='rewardTitle'
                    checked={!!formData.saveRewardTitle}
                    saveData={() => saveData('saveRewardTitle', !formData.saveRewardTitle)}
                  />
                </div>
              }
              <div className='buttons'>
                {
                  (loading)
                    ?
                    <button className='g-button med-btn'>
                      <ThreeDotsLoader />
                    </button>
                    :
                    <><button
                      className='g-button med-btn'
                      onClick={() => {
                        setShowModal(false);
                        setMode(null);
                      }}>
                      Cancel
                    </button>
                      <button
                        className='g-button med-btn'
                        onClick={() => savePreset()}>
                        Save
                      </button>
                    </>
                }
              </div>
            </div>
          </Modal>
          :
          (mode === 'load')
            ?
            <Modal
              show={showModal}
              cancel={() => setShowModal(false)}
              text="Load a Reward Preset" >
              <div className='load-div'>
                <div className='meta'>
                  {existingPresets ? Object.values(existingPresets).length : '0'}/100
                </div>
                <p>Select a reward preset for your players.</p>
                <div className='preset-list g-list-item'>
                  {
                    (existingPresets && Object.entries(existingPresets).length > 0)
                      ?
                      Object.entries(existingPresets)
                        .sort((a, b) => a[1].name.toLowerCase() > b[1].name.toLowerCase() ? 1 : -1)
                        .map(([key, preset]) => (
                          <div
                            key={key}
                            className={`preset-item ${selectedPreset === preset.id ? 'active' : ''}`}
                            onClick={() => choosePreset(preset)} >
                            <span className='preset-name'>{preset.name}</span>
                            <span className='meta'>{format(preset.ts_created, 'Pp')}</span>
                          </div>
                        ))
                      :
                      <div className='preset-item'>
                        <span className='meta'>No presets found...</span>
                      </div>
                  }
                </div>
                <div className="g-space-1"></div>
                <div className='buttons'>
                  {
                    (loading)
                      ?
                      <button className='g-button med-btn'>
                        <ThreeDotsLoader />
                      </button>
                      :
                      <>
                        <button
                          className='g-button med-btn'
                          onClick={() => {
                            setShowModal(false);
                            setMode(null);
                          }}>
                          Cancel
                        </button>
                        <button
                          className={`g-button med-btn ${selectedPreset ? '' : 'disabled'}`}
                          onClick={() => {
                            setPresetToLoad(chosenPresetRef.current);
                            setShowModal(false);
                          }}>
                          Load
                        </button>
                      </>
                  }
                </div>
              </div>
            </Modal>
            : null
      }
      <ModalAlert
        show={alertMessage}
        message={alertMessage}
        cancel={() => setAlertMessage(null)}
      />
    </div>
  )
}

export default RewardPresetModal